import axios from 'axios';
import concat from 'lodash/concat';
import uniqBy from 'lodash/uniqBy';
import cloneDeep from 'lodash/cloneDeep';
import { LAYER_COLORS } from '@/assets/dashboard/annotations';

export const state = () => ({
    layers: [],
    annotations: [],
    selectedLayers: [],
    layerProperties: {}
});

export const getters = {
    layers: state => state.layers,
    annotations: state => state.annotations,
    selectedLayers: state => state.selectedLayers,
    layerProperties: state => state.layerProperties,
    layerColors: state =>
        state.layerProperties.colors.map((color, index) => ({ id: color, value: LAYER_COLORS[index] }))
};

export const mutations = {
    SET_LAYERS(state, layers) {
        state.layers = layers;
    },

    ADD_LAYER(state, layer) {
        state.layers.push(layer);
    },

    UPDATE_LAYER(state, updatedLayer) {
        const index = state.layers.findIndex(({ id }) => id === updatedLayer.id);

        state.layers.splice(index, 1, updatedLayer);
    },

    REMOVE_LAYER(state, id) {
        const index = state.layers.findIndex(layer => layer.id === id);

        state.layers.splice(index, 1);
    },

    ADD_SELECTED_LAYER(state, layerId) {
        state.selectedLayers.push(layerId);
    },

    ADD_ANNOTATION(state, annotation) {
        state.annotations.push(annotation);
    },

    UPDATE_ANNOTATION(state, updatedAnnotation) {
        const index = state.annotations.findIndex(({ id }) => id === updatedAnnotation.id);

        state.annotations.splice(index, 1, updatedAnnotation);
    },

    REMOVE_ANNOTATION(state, id) {
        const index = state.annotations.findIndex(annotation => annotation.id === id);

        if (~index) {
            state.annotations.splice(index, 1);
        }
    },

    SET_SELECTED_LAYERS(state, selectedLayers) {
        state.selectedLayers = selectedLayers;
    },

    SET_LAYER_PROPERTIES(state, layerProperties) {
        state.layerProperties = layerProperties;
    },

    CONCAT_ANNOTATIONS(state, annotations) {
        const concattedAnnotations = concat(cloneDeep(state.annotations), annotations);

        const uniqueAnnotations = uniqBy(concattedAnnotations, 'id');

        state.annotations = uniqueAnnotations;
    },

    FILTER_VISIBLE_ANNOTATIONS(state) {
        state.annotations = state.annotations.filter(annotation => state.selectedLayers.includes(annotation.layerId));
    }
};

export const actions = {
    async index({ commit }, { layerIds }) {
        const params = {
            layerIds
        };

        // @TODO should allow to pass range in order to get annotations between selected [time range]
        const annotations = await axios.$get('/v1/annotations/annotations', { params });

        commit('CONCAT_ANNOTATIONS', annotations);
    },

    async store({ commit }, payload) {
        const annotation = await axios.$post('/v1/annotations/annotations', payload);

        commit('ADD_ANNOTATION', annotation);

        return annotation;
    },

    async update({ commit }, data) {
        const { id, name, text, anchorDate, layerId } = data;

        const payload = {
            name,
            text,
            anchorDate,
            layerId
        };

        await axios.$patch(`/v1/annotations/annotations/${id}`, payload);

        commit('UPDATE_ANNOTATION', data);
    },

    async delete({ commit }, id) {
        await axios.$delete(`/v1/annotations/annotations/${id}`);

        commit('REMOVE_ANNOTATION', id);
    },

    async getLayers({ commit }) {
        const layers = await axios.$get('/v1/annotations/layers');

        commit('SET_LAYERS', layers);
    },

    async storeLayer({ commit }, payload) {
        const layer = await axios.$post('/v1/annotations/layers', payload);

        commit('ADD_LAYER', layer);

        commit('ADD_SELECTED_LAYER', layer.id);
    },

    async updateLayer({ commit }, data) {
        await axios.$patch(`/v1/annotations/layers/${data.id}`, data);

        commit('UPDATE_LAYER', data);
    },

    async deleteLayer({ commit }, id) {
        await axios.$delete(`/v1/annotations/layers/${id}`);

        commit('REMOVE_LAYER', id);
    },

    async getLayerProperties({ commit }) {
        const { layerProperties } = await axios.$get('/v1/annotations/layers_properties');

        commit('SET_LAYER_PROPERTIES', layerProperties);
    },

    filterVisibleAnnotations({ commit }) {
        commit('FILTER_VISIBLE_ANNOTATIONS');
    },

    setSelectedLayers({ commit }, selectedLayers) {
        commit('SET_SELECTED_LAYERS', selectedLayers);
    }
};
